import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from '../env';
import { getLocalStorage } from '../utils/function/localStorage';

export const platformListingApi = createApi({
  reducerPath: 'platformListingApi',
  keepUnusedDataFor: 300,
  baseQuery: fetchBaseQuery({
    baseUrl: env.BASE_URL,
    prepareHeaders: (headers) => {
      headers.set("X-Auth-Token", getLocalStorage("tp_Aa_uth_access"));
      return headers;
    },
  }),
  tagTypes: ['Platforms', 'PlatformDetail', 'MyReferrals', 'PlatformReferrals'],

  endpoints: (builder) => ({

    // GET approved platforms list (search + category filter + pagination)
    getPlatforms: builder.query({
      query: ({ category = '', search = '', page = 1, limit = 20 } = {}) =>
        `/secure/platform/list?category=${category}&search=${search}&page=${page}&limit=${limit}`,
      providesTags: ['Platforms'],
    }),

    // GET single platform detail (includes reviews + approved referrals)
    getPlatformDetail: builder.query({
      query: (platformId) => `/secure/platform/${platformId}`,
      providesTags: (result, error, platformId) => [{ type: 'PlatformDetail', id: platformId }],
    }),

    // GET referral links for a platform (user-facing, excludes rejected)
    getPlatformReferrals: builder.query({
      query: ({ platformId, page = 1, limit = 20 }) => `/secure/platform/${platformId}/referrals?page=${page}&limit=${limit}`,
      providesTags: (result, error, { platformId }) => [{ type: 'PlatformReferrals', id: platformId }],
    }),

    // GET current user's own referral submissions + their status
    getMyReferrals: builder.query({
      query: () => '/secure/platform/my-referrals',
      providesTags: ['MyReferrals'],
    }),

    // GET current user's submitted platforms
    getMyPlatforms: builder.query({
      query: () => '/secure/platform/my-platforms',
      providesTags: ['Platforms'],
    }),

    // GET check if platform already exists by name or website
    checkPlatformExists: builder.query({
      query: ({ name = '', website = '' } = {}) =>
        `/secure/platform/check-exists?name=${encodeURIComponent(name)}&website=${encodeURIComponent(website)}`,
    }),

    // POST submit a new platform (goes to admin for approval)
    addPlatform: builder.mutation({
      query: (body) => ({
        url: '/secure/platform/add',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Platforms'],
    }),

    // POST add referral link/code to a platform
    addReferral: builder.mutation({
      query: ({ platformId, ...body }) => ({
        url: `/secure/platform/${platformId}/referral`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MyReferrals'],
    }),

    // POST add or update a review for a platform
    addReview: builder.mutation({
      query: ({ platformId, ...body }) => ({
        url: `/secure/platform/${platformId}/review`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { platformId }) => [
        { type: 'PlatformDetail', id: platformId },
      ],
    }),
    // GET current user's written reviews
    getMyReviews: builder.query({
      query: () => '/secure/platform/my-reviews',
    }),

    // GET platform group discussions the user has messages in
    getMyDiscussions: builder.query({
      query: () => '/secure/platform/my-discussions',
    }),

    // GET platform submission reward points received by current user
    getMyPlatformRewards: builder.query({
      query: () => '/secure/platform/my-rewards',
      providesTags: ['Platforms'],
    }),

    // GET approved platforms submitted by a specific user
    getUserApprovedPlatforms: builder.query({
      query: (username) => `/secure/platform/user/${username}/approved`,
      providesTags: ['Platforms'],
    }),

    // GET all platforms (with status filter, for platform moderator)
    getAllPlatformsAdmin: builder.query({
      query: ({ status = '', search = '', page = 1, limit = 20 } = {}) =>
        `/secure/platform/moderator/list?status=${status}&search=${search}&page=${page}&limit=${limit}`,
      providesTags: ['Platforms'],
    }),

    // UPDATE platform status (approve/reject) - for platform moderator
    updatePlatformStatus: builder.mutation({
      query: ({ platformId, status }) => ({
        url: `/secure/platform/${platformId}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Platforms'],
    }),

    // UPDATE platform credit status (pending/true/false) - for platform moderator / admin
    updatePlatformCreditStatus: builder.mutation({
      query: ({ platformId, creditStatus }) => ({
        url: `/secure/platform/${platformId}/status`,
        method: 'PUT',
        body: { creditStatus },
      }),
      invalidatesTags: ['Platforms'],
    }),

    // GET referrals for a platform (for platform moderator, supports status filter + pagination)
    getAdminPlatformReferrals: builder.query({
      query: ({ platformId, status, page = 1, limit = 20 }) =>
        `/secure/platform/${platformId}/referrals?status=${status || ""}&page=${page}&limit=${limit}`,
      providesTags: (result, error, { platformId }) => [{ type: 'PlatformReferrals', id: platformId }],
    }),
  }),
});

export const {
  useGetPlatformsQuery,
  useCheckPlatformExistsQuery,
  useLazyCheckPlatformExistsQuery,
  useGetPlatformDetailQuery,
  useGetPlatformReferralsQuery,
  useGetMyReferralsQuery,
  useGetMyPlatformsQuery,
  useAddPlatformMutation,
  useAddReferralMutation,
  useAddReviewMutation,
  useGetMyReviewsQuery,
  useGetMyDiscussionsQuery,
  useGetMyPlatformRewardsQuery,
  useGetUserApprovedPlatformsQuery,
  useLazyGetUserApprovedPlatformsQuery,
  useGetAllPlatformsAdminQuery,
  useUpdatePlatformStatusMutation,
  useUpdatePlatformCreditStatusMutation,
  useGetAdminPlatformReferralsQuery,
} = platformListingApi;
